import { useMemo, useState } from 'react';
import { formatDataLocal } from '@agencia/shared';
import { useCurrentUser } from '@/auth/AuthContext';
import { ConferenciaReservas } from '@/components/ConferenciaReservas';
import { Alert } from '@/components/ui';
import { VooCard } from '@/components/VooCard';
import { useApi } from '@/lib/useApi';
import type { ProximosVoos, VooProximo } from '@/lib/voos';

const PERIODOS = [
  { dias: 3, nome: 'Próximas 72 h' },
  { dias: 7, nome: '7 dias' },
  { dias: 15, nome: '15 dias' },
];

function nomeDia(dia: string, hoje: string): string {
  const d = Date.parse(`${dia}T00:00:00Z`);
  const h = Date.parse(`${hoje}T00:00:00Z`);
  const diff = Math.round((d - h) / 86400_000);
  if (diff === 0) return 'Hoje';
  if (diff === 1) return 'Amanhã';
  if (diff === -1) return 'Ontem';
  return formatDataLocal(dia);
}

/** Voos dos clientes que saem nos próximos dias: check-in, alterações e lembrete ao cliente. */
export function ProximosVoosPage() {
  const me = useCurrentUser();
  const gestao = me.role === 'ADMIN' || me.role === 'GESTOR';
  const [dias, setDias] = useState(3);
  const [soAlteracoes, setSoAlteracoes] = useState(false);
  const [consultor, setConsultor] = useState('');
  const [busca, setBusca] = useState('');
  const [aba, setAba] = useState<'voos' | 'conferencia'>('voos');
  const qs = new URLSearchParams({ dias: String(dias), ...(soAlteracoes ? { alteracoes: '1' } : {}), ...(consultor ? { consultor } : {}) });
  const { data, error, reload } = useApi<ProximosVoos>(`/crm/voos?${qs.toString()}`, 120_000);

  const consultores = useMemo(() => {
    const m = new Map<string, string>();
    for (const v of data?.voos ?? []) if (v.consultor) m.set(v.consultor.id, v.consultor.nome);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [data]);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!data) return [];
    if (!q) return data.voos;
    return data.voos.filter((v) => [v.cliente.nome, v.localizador, ...v.passageiros, v.origem, v.destino, `${v.companhia}${v.numeroVoo}`].some((s) => s.toLowerCase().includes(q)));
  }, [data, busca]);

  const porDia = useMemo(() => {
    const m = new Map<string, VooProximo[]>();
    for (const v of filtrados) {
      const dia = v.partida.slice(0, 10);
      m.set(dia, [...(m.get(dia) ?? []), v]);
    }
    return [...m.entries()];
  }, [filtrados]);

  const semCheckin = filtrados.filter((v) => !v.checkinFeitoEm).length;
  const hoje = data?.agora.slice(0, 10) ?? '';

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Próximos voos</h1>
          <p className="muted">
            Voos dos {gestao ? 'clientes da equipe' : 'seus clientes'} lidos dos bilhetes em PDF ou cadastrados no cliente.
            {data?.monitorLigado ? ' O sistema acompanha o status de cada voo e avisa aqui se algo mudar.' : ''}
          </p>
        </div>
        <div className="row tight" role="tablist" aria-label="Período">
          {PERIODOS.map((p) => (
            <button key={p.dias} type="button" role="tab" aria-selected={dias === p.dias} className={`chip ${dias === p.dias ? 'chip--orange' : 'chip--gray'}`} onClick={() => setDias(p.dias)}>
              {p.nome}
            </button>
          ))}
        </div>
      </header>

      <div className="tabs" role="tablist" aria-label="Visão">
        <button role="tab" type="button" aria-selected={aba === 'voos'} className="tab" onClick={() => setAba('voos')}>
          Voos
        </button>
        <button role="tab" type="button" aria-selected={aba === 'conferencia'} className="tab" onClick={() => setAba('conferencia')}>
          Conferência de reservas
        </button>
      </div>

      {error && <Alert>{error}</Alert>}
      {aba === 'voos' && data && !data.monitorLigado && gestao && (
        <Alert kind="info">O monitoramento de status dos voos ainda não está ligado: falta a chave do serviço AeroDataBox no servidor. Check-in e itinerário já funcionam.</Alert>
      )}

      <div className="row voos-filtros">
        <input className="search" type="search" placeholder="Buscar cliente, passageiro, localizador…" value={busca} onChange={(e) => setBusca(e.target.value)} aria-label="Buscar" />
        {gestao && (
          <select aria-label="Filtrar por consultor" value={consultor} onChange={(e) => setConsultor(e.target.value)}>
            <option value="">Todos os consultores</option>
            <option value="sem">Sem consultor</option>
            {consultores.map(([id, nome]) => (
              <option key={id} value={id}>
                {nome}
              </option>
            ))}
          </select>
        )}
        {aba === 'voos' && (
          <button type="button" className={`chip ${soAlteracoes ? 'chip--orange' : 'chip--gray'}`} aria-pressed={soAlteracoes} onClick={() => setSoAlteracoes((s) => !s)}>
            Alterações a ver{data && data.alteracoesPendentes > 0 ? ` (${data.alteracoesPendentes})` : ''}
          </button>
        )}
      </div>

      {aba === 'conferencia' ? (
        <ConferenciaReservas dias={dias} consultor={consultor} busca={busca} mostrarConsultor={gestao} gestao={gestao} onConferiu={() => void reload()} />
      ) : !data ? (
        <p className="muted">Carregando…</p>
      ) : filtrados.length === 0 ? (
        <section className="card">
          <p className="muted">
            {soAlteracoes
              ? 'Nenhuma alteração pendente. 👍'
              : `Nenhum voo ${busca ? 'encontrado' : `nos próximos ${dias} dias`}. Os voos aparecem aqui quando um bilhete em PDF é salvo no cliente (pela Digisac ou anexado à mão) ou quando a reserva é cadastrada na página do cliente.`}
          </p>
        </section>
      ) : (
        <>
          <p className="muted small">
            {filtrados.length} voo(s) · {semCheckin} sem check-in
          </p>
          {porDia.map(([dia, voos]) => (
            <section key={dia} className="voos-dia">
              <h2 className="voos-dia__titulo">
                <span className="voos-dia__nome">{nomeDia(dia, hoje)}</span> <span className="muted small">· {voos.length} voo(s)</span>
              </h2>
              <div className="voos-grid">
                {voos.map((v) => (
                  <VooCard key={v.id} v={v} monitorLigado={data.monitorLigado} mostrarConsultor={gestao} onChanged={() => void reload()} />
                ))}
              </div>
            </section>
          ))}
        </>
      )}
    </div>
  );
}
